/**
 * @module nhcmap/MapManager
 */
import { Map as olMap, View } from 'ol';
import { fromLonLat } from 'ol/proj';
import MapControls from './MapControls.js';
import MapInteractions from './MapInteractions.js';
import ProductManager from './ProductManager.js';
import { getStateAbbrLayer } from './layers/getStateAbbrLayer.js';
import { loadConfig } from './utils/loadUtils.js';
import { initI18n, mergeProductLanguages } from './utils/i18n.js';

class MapManager {
  
  constructor(products = [], params) {
    this.products = products;
    this.productManager = null;
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        this[key] = params[key];
      }
    }
    this.map = null;
    this.autoZoom = params.autoZoom || false;
    this.latOffset = params.latOffset || 0;
    this.displayDropdown = params.displayDropdown || false;
    
    // Set language and make sure it's one we support
    this.languages = ["en", "es"];
    this.language = params.language || 'en';
    if (!this.languages.includes(this.language)) {
      this.language = 'en';
    }
  }

  /**
   * Creates a new MapManager instance.
   * @param {Array} products 
   * @param {object} params 
   * @returns {Promise<MapManager>} The initialized MapManager instance
   */
  static async create(products = [], params = {}) {
    const mapManager = new MapManager(products, params);

    // This loads the language data before any components are created.
    await initI18n(mapManager.language);

    await mapManager.initialize();

    // Load all product configurations
    const configs = await mapManager.loadAllProductConfigs();

    // MERGE all product language files into the i18n system
    await mergeProductLanguages(configs, mapManager.language);

    await mapManager.productManager.addProductsFromConfigs(configs);
    await mapManager.mapControls.loadControlsFromConfigs(configs, mapManager);
    mapManager.mapInteractions.loadInteractionsFromConfigs(configs, mapManager);

    if (mapManager.displayDropdown) {
      await mapManager.loadSelectProductControl();
    }
    // Set up periodic updates
    const updateInterval = 5 * 60 * 1000; // 5 minutes in milliseconds
    setInterval(() => mapManager.updateProducts(), updateInterval);
    return mapManager;
  }

  /**
   * Initialize the map
   * @returns {Promise<void>} All async methods return a promise
   */
  async initialize() {

    // Import core layers
    const { default: CoreLayers } = await import('./layers/CoreLayers.js');
    const coreLayers = new CoreLayers(this.language);

    // Create the map
    this.map = new olMap({
      controls: [
        coreLayers.zoomControl,
        coreLayers.attributionControl
      ],
      layers: [
        coreLayers.baseLayerGroup,
        coreLayers.borderLayer,
        coreLayers.labelLayer,
        coreLayers.graticule
      ],
      overlays: [],
      target: 'map',
      view: new View({
        center: fromLonLat([-87.0, 33.9]), // was -82.0, 33.9
        zoom: 4,
        minZoom: 2,
        maxZoom: 10
      }),
    });

    // Add abbreviations for states
    const stateAbbrLayer = getStateAbbrLayer(this.map, true, "16px");
    stateAbbrLayer.set('name', 'stateAbbrLayer');
    this.map.addLayer(stateAbbrLayer);

    this.productManager = new ProductManager(this.map, this.language);
    this.mapControls = new MapControls();
    this.mapInteractions = new MapInteractions();
  }

  /**
   * Loads and merges configurations for all active products.
   */
  async loadAllProductConfigs() {
    const promises = this.products
      .filter(p => p.checked)
      .map(p => loadConfig(`/maps/${p.id}/${p.id}.json`, this.language));
    
    return Promise.all(promises);
  }

  /**
   * Loads the product selection control. This is done here because the
   * control is not part of any one product, but rather a global control.
   * It also uses a conditional import to avoid loading it unnecessarily.
   * @returns {Promise<void>}
   */
  async loadSelectProductControl() {
    const { default: SelectProductControl } = await import("./controls/SelectProductControl.js");
    const control = new SelectProductControl({
      target: document.getElementById('map'),
      mapManager: this,
      products: this.products
    });
    this.map.addControl(control);
  }

  /**
   * Adds a product to the map.
   * @param {string} productId 
   */
  async addProduct(productId) {
    await this.productManager.addProduct(productId);
    this.products.push({ id: productId, name: productId, checked: true });
  }

  /**
   * Removes a product from the map.
   * @param {string} productId 
   */
  async removeProduct(productId) {
    await this.productManager.removeProduct(productId);
    this.products = this.products.filter(id => id !== productId);
  }

  /**
   * Updates all products on the map.
   */
  async updateProducts() {
    console.log("Updating products...");
    for (const product of this.products) {
      if (product.checked) {
        const productId = product.id;
        await this.productManager.updateProduct(productId);
      }
    }
  }

  /**
   * Changes the map's language and reloads all language-dependent layers.
   * @param {string} newLanguage - The new two-letter language code (e.g., 'es').
   * @returns {Promise<void>}
   */
  async changeLanguage(newLanguage) {
    if (!this.languages.includes(newLanguage) || newLanguage === this.language) {
      return; // Do nothing if language is unsupported or unchanged
    }

    this.language = newLanguage;

    // Re-initialize internationalization for the new language
    await initI18n(this.language);
    const configs = await this.loadAllProductConfigs();
    await mergeProductLanguages(configs, this.language);

    // Update the ProductManager's language
    this.productManager.setLanguage(newLanguage);

    // Re-create and replace the core language-dependent layers
    await this.reloadCoreLayers();

    // Tell the ProductManager to reload all currently active products
    await this.productManager.reloadLanguageProducts();

    // Tell the existing controls to update their text (lightweight)
    this.mapControls.updateControlLanguages();
  }

  /**
   * Helper method to reload and replace core layers that depend on language.
   * @private
   */
  async reloadCoreLayers() {

    // 1. Find and remove the old language-dependent controls
    const coreControlNames = ['nhcCoreZoom', 'nhcCoreAttribution'];
    const controlsToRemove = this.map.getControls().getArray()
      .filter(control => coreControlNames.includes(control.get('name')));
    controlsToRemove.forEach(control => this.map.removeControl(control));

    // 2. Find and remove the old language-dependent layers
    const layersToRemove = this.map.getLayers().getArray()
      .filter(layer => ['nhcLabelLayer', 'nhcGraticule'].includes(layer.get('name')));
    layersToRemove.forEach(layer => this.map.removeLayer(layer));

    // Create new instances with the updated language
    const { default: CoreLayers } = await import('./layers/CoreLayers.js');
    const coreLayers = new CoreLayers(this.language);
    this.map.addControl(coreLayers.zoomControl);
    this.map.addControl(coreLayers.attributionControl);
    this.map.addLayer(coreLayers.labelLayer);
    this.map.addLayer(coreLayers.graticule);
  }

}

export default MapManager;